import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Utils } from '@helpers/utils';
import { AlarmLevel } from 'ops/common/models/alarm-level';
import { Channel, ChannelFns, FlagOption } from 'ops/common/models/channel';

const alarmLevelToTextClassMap = new Map();
alarmLevelToTextClassMap.set(AlarmLevel.Warning, 'text-warning');
alarmLevelToTextClassMap.set(AlarmLevel.Caution, 'text-caution');
alarmLevelToTextClassMap.set(AlarmLevel.Alarm, 'text-alarm');

const alarmLevelToBorderMap = new Map();
alarmLevelToBorderMap.set(AlarmLevel.Warning, 'border-warning');
alarmLevelToBorderMap.set(AlarmLevel.Caution, 'border-caution');
alarmLevelToBorderMap.set(AlarmLevel.Alarm, 'border-alarm');

@Component({
    selector: 'app-alarm-indicator',
    templateUrl: './alarm-indicator.component.html',
    styleUrls: ['./alarm-indicator.component.scss'],
    standalone: false,
})
export class AlarmIndicatorComponent {
    @Input() channel: Channel;
    @Input() value: number;
    @Input() statusString: string = '';
    @Input() status: FlagOption;
    @ViewChild('valueText', { static: true }) valueText: ElementRef;

    alarmTextClass: string;
    alarmBorderClass: string;
    valueChangeDirection: number;

    alarmStateToTextClassMap = new Map();
    alarmStateToBorderMap = new Map();

    valueContainerLeft: number = 0;

    constructor() {}

    ngOnInit(): void {
        this.initializeAlarmDisplays();
        this.updateDisplayToLatestValue();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.channel) {
            return;
        }

        if (
            changes.value &&
            !Utils.isNullOrUndefined(changes.value.previousValue) &&
            !Utils.isNullOrUndefined(changes.value.currentValue)
        ) {
            if (changes.value.previousValue > changes.value.currentValue) {
                this.valueChangeDirection = -1;
            } else if (changes.value.previousValue < changes.value.currentValue) {
                this.valueChangeDirection = 1;
            } else {
                this.valueChangeDirection = 0;
            }
        }

        if (changes.channel) {
            this.initializeAlarmDisplays();
        }
        this.updateDisplayToLatestValue();
    }

    updateDisplayToLatestValue() {
        this.valueText.nativeElement.innerHTML = ChannelFns.scaleValue(this.channel, this.value);
        this.updateStyles(this.status);
    }

    updateStyles(status: FlagOption) {
        this.alarmTextClass = this.alarmStateToTextClassMap.get(status);
        this.alarmBorderClass = this.alarmStateToBorderMap.get(status);
    }

    initializeAlarmDisplays() {
        this.alarmStateToTextClassMap.set(
            FlagOption.LOW_LOW,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingVeryLow.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(
            FlagOption.LOW,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingLow.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(
            FlagOption.FEEDBACK,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingFeedbackError.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(FlagOption.NORMAL, 'text-normal');
        this.alarmStateToTextClassMap.set(
            FlagOption.HIGH,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingHigh.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(
            FlagOption.HIGH_HIGH,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        );
        this.alarmStateToTextClassMap.set(FlagOption.UNDEFINED, 'text-gap');

        this.alarmStateToBorderMap.set(
            FlagOption.LOW_LOW,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingVeryLow.alarmLevel),
        );
        this.alarmStateToBorderMap.set(
            FlagOption.LOW,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingLow.alarmLevel),
        );
        this.alarmStateToBorderMap.set(
            FlagOption.FEEDBACK,
            alarmLevelToTextClassMap.get(this.channel.fcuData.alarmSettingFeedbackError.alarmLevel),
        );

        this.alarmStateToBorderMap.set(FlagOption.NORMAL, 'border-normal');
        this.alarmStateToBorderMap.set(
            FlagOption.HIGH,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingHigh.alarmLevel),
        );
        this.alarmStateToBorderMap.set(
            FlagOption.HIGH_HIGH,
            alarmLevelToBorderMap.get(this.channel.fcuData.alarmSettingVeryHigh.alarmLevel),
        );
        this.alarmStateToBorderMap.set(FlagOption.UNDEFINED, 'border-gap');
    }
}
